import { Injectable } from "@angular/core";
import { environment } from "@app/../environments/environment";
import { TranslateService } from "@ngx-translate/core";
import { AuctionStatus, AuctionStatusType, GenericConstants, SessionKey } from "@shared/clickar-constants";
import { AuctionDTO } from "@shared/model/auction.model";
import { UserDTO } from "@shared/model/user.model";
import { CodeTable } from "@shared/services/code-table/code-table.model";
import { StorageManagerService } from "@shared/services/storage/storage-manager.service";

@Injectable({
	providedIn: "root"
})
export class ClickarUtilsService {
	constructor(
		private storageService: StorageManagerService,
		private translate: TranslateService
	) { }

	isNullOrUndefined = (obj: any): boolean =>
		this.isUndefined(obj) || this.isNull(obj);

	groupBy = (array, key) => {
		// Return the end result
		return array.reduce((result, currentValue) => {
			// If an array already present for key, push it to the array. Else create an array and push the object
			(result[currentValue[key]] = result[currentValue[key]] || []).push(
				currentValue
			);
			// Return the current iteration `result` value, this will be taken as next iteration `result` value and accumulate
			return result;
		}, {}); // empty object is the initial value for result object
	};

	notNullOrUndefined = (obj: any): boolean => !this.isNullOrUndefined(obj);

	isNull = (obj: any): boolean => obj === null;

	notNull = (obj: any): boolean => !this.isNull(obj);

	isUndefined = (obj: any): boolean => obj === undefined;

	notUndefined = (obj: any): boolean => !this.isUndefined(obj);

	deepCopy = (source: any): any =>
		this.isNullOrUndefined(source)
			? source
			: JSON.parse(JSON.stringify(source));

	isJsonString(str: string) {
		try {
			JSON.parse(str);
		} catch (e) {
			return false;
		}
		return true;
	}

	zeroLeft(num: number | string, size: number): string {
		if (this.isEmpty(num)) {
			return null;
		}
		if (String(num).length >= size) {
			return String(num);
		}
		return (
			Array.apply(null, { length: size })
				.map(Function.call, () => "0")
				.join("")
				.substr(String(num).length) + String(num)
		);
	}

	initializeArray(array: any[], length: number, type: any): any[] {
		const arr = [],
			arrayLen = this.isEmpty(array) ? 0 : array.length;
		for (let i = 0; i < Math.max(length, arrayLen); i++) {
			if (
				this.isNullOrUndefined(array) ||
				this.isNullOrUndefined(array[i])
			) {
				arr.push(new type());
			} else {
				arr.push(Object.assign(new type(), array[i]));
			}
		}
		return arr;
	}

	removeEmpty = (array: any[]): any[] =>
		this.isEmpty(array) ? [] : array.filter(el => !this.isEmpty(el));

	hasPermission(toFind: string, permissions: string[]): boolean {
		if (permissions && toFind) {
			const perm = permissions.find(value => value === toFind);
			return !this.isNullOrUndefined(perm);
		}
		return false;
	}

	isEmpty(obj: any): boolean {
		if (this.isNullOrUndefined(obj)) {
			return true;
		} else if (typeof obj === "string") {
			return obj === "";
		} else if (typeof obj === "number") {
			return isNaN(obj) || !isFinite(obj);
		} else if (Array.isArray(obj)) {
			return (obj).length === 0;
		}
	}

	notEmpty = (obj: any): boolean => !this.isEmpty(obj);

	calculateMovementIE(event: MouseEvent, prev: { x?: number; y?: number }) {
		const mov: { x?: number; y?: number } = {};
		mov.x = prev.x ? event.screenX - prev.x : 0;
		mov.y = prev.y ? event.screenY - prev.y : 0;
		prev.x = event.screenX;
		prev.y = event.screenY;
		return mov;
	}

	strToBase64 = (s: string): string => this.isNullOrUndefined(s) ? s : btoa(encodeURIComponent(s));

	getPreferredLanguage = (): string =>
		(this.storageService.hasSessionItem(SessionKey.USER_MARKET)) ?
			(this.storageService.getSessionItem(SessionKey.USER_PROFILE) as UserDTO).preferredLanguage :
			this.translate.getCurrentLang().slice(0, 2).toUpperCase();

	checkFiscalCodeDate(cf: string, birthDate: Date): boolean {
		const months = {
			A: 0,
			B: 1,
			C: 2,
			D: 3,
			E: 4,
			H: 5,
			L: 6,
			M: 7,
			P: 8,
			R: 9,
			S: 10,
			T: 11
		},
			year = cf.substring(6, 8),
			month = cf.substring(8, 9),
			day = cf.substring(9, 11);

		let valid = true,
			yearBirth = `${birthDate.getFullYear()}`;
		yearBirth = yearBirth.substring(2, 4);

		if (year !== yearBirth) {
			valid = false;
		}
		const monthBirth = birthDate.getMonth();

		if (months[month.toUpperCase()] != monthBirth) {
			valid = false;
		}

		let dayBirth = birthDate.getDate();

		if (dayBirth > 40) {
			dayBirth -= 40;
		}
		if (day != dayBirth + "") {
			valid = false;
		}
		return valid;
	}

	getMarket = (): string =>
		this.storageService.hasSessionItem(SessionKey.USER_MARKET)
			? (
				this.storageService.getSessionItem(
					SessionKey.USER_PROFILE
				) as UserDTO
			).idMarket
			: "";

	base64ToStr = (s: string): string => this.isNullOrUndefined(s) ? s : decodeURIComponent(atob(s));

	formatNumber(
		value: number,
		decimals: number = GenericConstants.NUM_OF_DECIMALS,
		mileageSeparator: string = GenericConstants.MILEAGE_SEPARATOR,
		decimalSeparator: string = GenericConstants.DECIMAL_SEPARATOR,
		symbol = ""
	): string {
		const decSeparator = this.decodeDecSeparator(
			this.storageService.getSessionItem(
				SessionKey.USER_MARKET,
				decimalSeparator
			),
			decimalSeparator
		),
			milSeparator = this.decodeMilSeparator(
				this.storageService.getSessionItem(
					SessionKey.USER_MARKET,
					mileageSeparator
				),
				mileageSeparator
			);
		value =
			value != null
				? Math.sign(value) *
				parseFloat(Math.abs(value).toString().replace(/\D/g, "."))
				: 0;
		value = isNaN(value) ? 0 : value;
		const sign = String(Math.sign(value)).replace(/\d/g, ""),
			re = "\\d(?=(\\d{3})+" + (decimals > 0 ? "\\D" : "$") + ")",
			num = Math.abs(value).toFixed(Math.max(0, Math.floor(decimals)));
		return (
			sign +
			(decSeparator
				? num.replace(new RegExp("[\.]", "g"), decSeparator)
				: num
			).replace(new RegExp(re, "g"), "$&" + milSeparator) +
			" " +
			symbol
		);
	}

	parseNumber(
		value: string,
		mileageSeparator: string = GenericConstants.MILEAGE_SEPARATOR
	): string {
		const milSeparator = this.decodeMilSeparator(
			this.storageService.getSessionItem(
				SessionKey.USER_MARKET,
				mileageSeparator
			),
			mileageSeparator
		);
		return value
			? parseFloat(
				value
					.replace(new RegExp(`\\${milSeparator}|\\D+$`, "g"), "")
					.replace(/\D/g, ".")
			).toString()
			: "";
	}

	private decodeDecSeparator(val: string, def: string): string {
		switch (val) {
			case "PL":
				return ",";
			case "FR":
				return ",";
			default:
				return def;
		}
	}

	private decodeMilSeparator(val: string, def: string): string {
		switch (val) {
			case "PL":
				return ".";
			case "FR":
				return " ";
			default:
				return def;
		}
	}

	hasRole(role: string, roles: string[]): boolean {
		if (roles && role) {
			const r = roles.find(value => value == role);
			return !this.isNullOrUndefined(r);
		}
		return false;
	}

	getImageUrl = (idImage: number) => `${environment.baseImageUrl}/images?idImage=${idImage || ""}`;

	getOpenImageUrl = this.getImageUrl;

	getTempImageUrl = (idImage: number) => `${environment.baseApiUrl}/api/v1/open/temp-image?idImage=${idImage || ""}`;

	getPDFUrl = (idPdf: number) => `${environment.baseApiUrl}/api/v1/secure/pdf?idImage=${idPdf}`;

	isItalian = (): boolean => this.getMarket() === "IT";
	isFrench = (): boolean => this.getMarket() === "FR";
	isGerman = (): boolean => this.getMarket() === "DE";
	isEngland = (): boolean => this.getMarket() === "EN";

	/** Tells whether the provided Auction is on Extra time
	 * (that is: it's running, but original time has expired) */
	isExtraTime = (auction: AuctionDTO): boolean =>
		auction.status === AuctionStatus.RUNNING &&
		/* now */ Date.now() >=
			/* end */ new Date(auction.endDate).getTime();

	getLotStatus = (lot: { active: boolean }) =>
		lot.active === false ? // NOTE: in case it's null, the lot is open
			AuctionStatus.CLOSED : AuctionStatus.RUNNING;

	/** Tells whether the lot is on extra time (that is, its due date is later than auction's due date. */
	isLotExtra = (lot: { dateEnd: Date; }, auction: AuctionDTO) => new Date(lot.dateEnd) > new Date(auction.endDate);

	/* Returns lot badge's status color */
	getStatusColor = (status: AuctionStatusType | "EXTRA", type: "auction" | "lot" = "lot") =>
		status === AuctionStatus.RUNNING
			? type === "lot" ? "outline" : "lightblue"
			: status === "EXTRA"
				? "red"
				: "grey"; // Grey: SCHEDULED, CLOSED. Red: Extra

	// /** The auction may be running, scheduled and closed.\
	//  * In the latest case, if there is still an open lot, it's Extra time */
	// getAuctionStatus = (auction: AuctionDTO): AuctionStatusType | "EXTRA" => this.isExtraTime(auction) ? "EXTRA" : auction.status;
	getLinkFromCt(
		codetable: CodeTable[],
		appraisalCode: string,
		licensePlate: string
	): string {
		const ct = codetable.find(codeTable => codeTable.elementCode === `${appraisalCode}_240x180`);
		if (ct) {
			let link = ct.elementDes;
			link = link.replace("#LICENSEPLATE#", licensePlate);
			return link;
		} else {
			return null;
		}
	}
}